import { createContext, useContext, useMemo, type ReactNode } from "react";

import { useOrganization } from "@/hooks/settings/useOrganization";
import type { Organization } from "@/types/supabase.ext";

export type OrgFeature = "ai-docs" | "settings" | "procurement" | "knowledge_base";

interface OrgFeatureFlagState {
  features: OrgFeature[];
  hasFeature: (feature: OrgFeature) => boolean;
  isLoading: boolean;
}

const ALL_FEATURES: OrgFeature[] = ["procurement", "settings", "ai-docs", "knowledge_base"];

// Org-specific feature overrides (takes precedence over defaults)
// These are for organizations that need special feature combinations
const ORG_FEATURE_OVERRIDES: Record<string, OrgFeature[]> = {
  "4703b52a-0179-47e6-89aa-12dffe4d8e4a": ALL_FEATURES,
  "4e605c8a-689e-488b-ad3f-c6a3c92b24b0": ALL_FEATURES,
  "1d57394a-f11e-4704-8de4-16129900b8b5": ALL_FEATURES,
  "b99e7e6a-ad2c-4626-8b8f-167b826694aa": ALL_FEATURES,
};

const OrgFeatureFlagsContext = createContext<OrgFeatureFlagState | undefined>(undefined);

/**
 * Build feature set from organization data
 *
 * Priority:
 * 1. Org-specific overrides (ORG_FEATURE_OVERRIDES) - for special cases
 * 2. Database fields (e.g., report_tab_enabled) - actual feature flags
 * 3. Default: settings only (most restrictive)
 */
const buildFeatureSet = (organization: Organization | undefined): OrgFeature[] => {
  if (!organization?.id) {
    // Not loaded yet - return minimal features
    return ["settings"];
  }

  // Check for org-specific overrides first
  const override = ORG_FEATURE_OVERRIDES[organization.id];
  if (override) {
    return override;
  }

  // Build features from database fields
  const features: OrgFeature[] = ["settings"]; // Always enabled

  // report_tab_enabled controls the "ai-docs" feature
  if (organization.report_tab_enabled) {
    features.push("ai-docs");
  }

  // procurement_enabled controls the "procurement" feature
  if (organization.procurement_enabled) {
    features.push("procurement");
  }

  // knowledge_base_enabled controls the "knowledge_base" feature
  if (organization.knowledge_base_enabled) {
    features.push("knowledge_base");
  }

  return features;
};

export const OrgFeatureFlagsProvider = ({ children }: { children: ReactNode }) => {
  const { data: organization, isLoading } = useOrganization();

  const value = useMemo<OrgFeatureFlagState>(() => {
    const featureSet = buildFeatureSet(organization);
    const hasFeature = (feature: OrgFeature) => featureSet.includes(feature);
    return {
      features: featureSet,
      hasFeature,
      isLoading,
    };
  }, [organization, isLoading]);

  return (
    <OrgFeatureFlagsContext.Provider value={value}>{children}</OrgFeatureFlagsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useOrgFeatureFlags = (): OrgFeatureFlagState => {
  const context = useContext(OrgFeatureFlagsContext);
  if (!context) {
    throw new Error("useOrgFeatureFlags must be used within an OrgFeatureFlagsProvider");
  }
  return context;
};
